import { getContentWidthPixels } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/sectionContentWidth";
import {
  getConsolidatedSpacingValues,
  getResponsiveSpacingPixels,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/spacing";
import {
  assignInlineVars,
  DynamicVariable,
  mapResponsivePropToCSSVars,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { getColor } from "@faire/sdui/lib/getColor";
import { ISectionStyle } from "@faire/web-api--source/indigofair/data/ISectionStyle";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
import { Column } from "@faire/web/slate/Layout";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { cn } from "@faire/web/tools/cn";
import React from "react";

const varMobilePadding: DynamicVariable<"--f-sdui-section-padding"> = {
  variable: "--f-sdui-section-padding",
  className: "p-(--f-sdui-section-padding)",
};

const varTabletPadding: DynamicVariable<"--f-sdui-section-padding-md"> = {
  variable: "--f-sdui-section-padding-md",
  className: "md:p-(--f-sdui-section-padding-md)",
};

const varDesktopPadding: DynamicVariable<"--f-sdui-section-padding-lg"> = {
  variable: "--f-sdui-section-padding-lg",
  className: "lg:p-(--f-sdui-section-padding-lg)",
};

const varXLargePadding: DynamicVariable<"--f-sdui-section-padding-xl"> = {
  variable: "--f-sdui-section-padding-xl",
  className: "xl:p-(--f-sdui-section-padding-xl)",
};

export interface ISDUISectionStyleWrapperProps {
  sectionStyles: ISectionStyle | undefined;
  /**
   * Whether the section should be visible on a specific viewport.
   * By default, the section will be visible on all viewports.
   */
  sectionVisibility?: ResponsiveValues<boolean>;
}

export const SDUISectionStyleWrapper: React.FC<
  React.PropsWithChildren<ISDUISectionStyleWrapperProps>
> = ({ sectionStyles, sectionVisibility, children }) => {
  const clientTopPadding = getResponsiveSpacingPixels(
    Spacing.SPACING_NONE,
    sectionStyles?.responsive_top_padding
  );
  const clientHorizontalPadding = getResponsiveSpacingPixels(
    Spacing.SPACING_NONE,
    sectionStyles?.responsive_horizontal_padding
  );
  const clientBottomPadding = getResponsiveSpacingPixels(
    Spacing.SPACING_NONE,
    sectionStyles?.responsive_bottom_padding
  );
  const responsivePaddingShorthand = getConsolidatedSpacingValues(
    clientTopPadding,
    clientBottomPadding,
    clientHorizontalPadding,
    clientHorizontalPadding
  );
  const { classes: paddingClassnames, vars: paddingStyles } =
    mapResponsivePropToCSSVars(responsivePaddingShorthand, {
      mobileAndAbove: varMobilePadding,
      tabletAndAbove: varTabletPadding,
      desktopAndAbove: varDesktopPadding,
      xLargeAndAbove: varXLargePadding,
    });

  return (
    <Column
      align="center"
      justify="center"
      className="w-full"
      style={{
        backgroundColor:
          getColor(sectionStyles?.background_color) ?? "transparent",
      }}
    >
      <Column
        data-test-id="section-style-content-wrapper"
        align="center"
        justify="center"
        className={cn(
          "w-full",
          paddingClassnames,
          ...getSectionVisibilityClassNames(sectionVisibility)
        )}
        style={{
          maxWidth: getContentWidthPixels(sectionStyles?.content_width),
          ...assignInlineVars(paddingStyles),
        }}
      >
        {children}
      </Column>
    </Column>
  );
};

const getSectionVisibilityClassNames = (
  sectionVisibility: ResponsiveValues<boolean> | undefined
) => {
  if (!sectionVisibility) {
    return [];
  }

  return Object.entries(sectionVisibility).map(([viewport, shouldShow]) => {
    return shouldShow
      ? VISIBLE_CLASS_NAMES[viewport as keyof ResponsiveValues<string>]
      : HIDDEN_CLASS_NAMES[viewport as keyof ResponsiveValues<string>];
  });
};

const VISIBLE_CLASS_NAMES: ResponsiveValues<string> = {
  mobileAndAbove: "flex",
  tabletAndAbove: "md:flex",
  desktopAndAbove: "lg:flex",
};

const HIDDEN_CLASS_NAMES: ResponsiveValues<string> = {
  mobileAndAbove: "hidden",
  tabletAndAbove: "md:hidden",
  desktopAndAbove: "lg:hidden",
};
